import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { Skeleton } from '@/components';
import { TABS_TEST_IDS } from '@/constants/data-testids.ts';
import { formatNumberWithComma } from '@/utils/numberFormatter';
import { cn } from '@/utils/tailwind.ts';

export type TabItem = {
  value: string;
  label: string;
  content: ReactNode;
  /** Badge value; null renders an em dash for a not-yet-known count, undefined hides the badge. */
  counter?: number | null;
  counterIcon?: ReactNode;
  isInteractive?: boolean;
};

type TabsProps = ComponentProps<typeof TabsPrimitive.Root> & {
  items: TabItem[];
  isCounterLoading?: boolean;
  tabsLeftContent?: ReactNode;
  tabsRightContent?: ReactNode;
  tabsClassName?: string;
  containerClassName?: string;
  tabListClassName?: string;
  tabsContentClassName?: string;
};
type TabsListProps = ComponentProps<typeof TabsPrimitive.List>;
type TabsTriggerProps = ComponentProps<typeof TabsPrimitive.Trigger> & {
  counter?: number | null;
  counterIcon?: ReactNode;
  isCounterLoading?: boolean;
  interactive?: boolean;
};
type TabsContentProps = ComponentProps<typeof TabsPrimitive.Content>;

const Tabs = ({
  items,
  isCounterLoading,
  tabsLeftContent,
  tabsRightContent,
  className,
  tabsClassName,
  containerClassName,
  tabListClassName,
  tabsContentClassName,
  orientation = 'horizontal',
  ...props
}: TabsProps) => {
  return (
    <TabsPrimitive.Root defaultValue={items[0]?.value} className={className} {...props}>
      <div
        className={cn(
          'flex flex-col gap-5',
          orientation === 'horizontal' ? 'flex-col' : 'flex-row',
          containerClassName
        )}
      >
        <div className={cn('flex items-center', tabsClassName)}>
          {tabsLeftContent}
          <TabsList className={cn('select-none', tabListClassName)} orientation={orientation}>
            {items.map(({ value, label, counter, counterIcon, isInteractive = true }) => (
              <TabsTrigger
                key={value}
                value={value}
                counter={counter}
                counterIcon={counterIcon}
                isCounterLoading={isCounterLoading}
                interactive={isInteractive}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabsRightContent}
        </div>
        {items.map(({ value, content }) => (
          <TabsContent key={value} value={value} className={cn('w-full', tabsContentClassName)}>
            {content}
          </TabsContent>
        ))}
      </div>
    </TabsPrimitive.Root>
  );
};

const TabsList = ({
  className,
  children,
  orientation = 'horizontal',
  ...props
}: TabsListProps & { orientation?: 'horizontal' | 'vertical' }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, top: 0, width: 0, height: 0 });

  const updateIndicator = () => {
    const active = listRef.current?.querySelector('[data-state="active"]') as HTMLElement;
    if (active && listRef.current) {
      const listRect = listRef.current.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();

      setIndicatorStyle(
        orientation === 'vertical'
          ? {
              top: activeRect.top - listRect.top,
              height: activeRect.height,
              left: 0,
              width: listRef.current.clientWidth,
            }
          : {
              left: activeRect.left - listRect.left,
              width: activeRect.width,
              top: 0,
              height: listRef.current.clientHeight,
            }
      );
    }
  };

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, orientation]);

  useEffect(() => {
    const observer = new MutationObserver(updateIndicator);
    if (listRef.current) observer.observe(listRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative overflow-auto no-scrollbar px-[1px] py-[1px]">
      <div className="relative">
        <TabsPrimitive.List
          ref={listRef}
          data-slot="tabs-list"
          className={cn(
            'flex relative min-w-max min-h-12 items-center',
            orientation === 'vertical' ? 'flex-col' : 'flex-row gap-2',
            className
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
        <div
          data-testid={TABS_TEST_IDS.INDICATOR}
          className="absolute rounded-[6px] shadow-sm border border-custom-warm-grey-200 bg-custom-warm-grey-25 transition-all duration-300"
          style={
            orientation === 'vertical'
              ? { top: indicatorStyle.top, height: indicatorStyle.height, left: 0, right: 0 }
              : { left: indicatorStyle.left, width: indicatorStyle.width, top: 0, bottom: 0 }
          }
        />
      </div>
    </div>
  );
};

const TabsTrigger = ({
  className,
  children,
  counter,
  counterIcon,
  isCounterLoading,
  interactive = true,
  ...props
}: TabsTriggerProps) => {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      disabled={!interactive}
      className={cn(
        'relative z-10 text-body-14-medium flex items-center gap-3 px-3 py-2.5 disabled:pointer-events-none disabled:opacity-100',
        interactive
          ? '!text-custom-warm-grey-500 cursor-pointer data-[state=active]:!text-custom-warm-grey-800'
          : 'cursor-default !text-custom-warm-grey-800',
        className
      )}
      {...props}
    >
      {children}
      {counter !== undefined && (
        <div className="flex items-center gap-1 min-w-[24px]">
          <span className="text-custom-warm-grey-400">{counterIcon}</span>

          {isCounterLoading ? (
            <Skeleton className="w-[40px] h-[20px]" />
          ) : (
            <span className="text-custom-warm-grey-500">
              {counter === null ? '—' : formatNumberWithComma(counter)}
            </span>
          )}
        </div>
      )}
    </TabsPrimitive.Trigger>
  );
};

const TabsContent = ({ className, ...props }: TabsContentProps) => {
  return <TabsPrimitive.Content data-slot="tabs-content" className={className} {...props} />;
};

export { Tabs, TabsContent, TabsList, TabsTrigger };
